import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { AppToaster } from '@crowdex/ui/components/toaster';
import { type ReactNode, useState } from 'react';
import { AppHomePage } from '@/components/app-home-page';
import { ArtifactsPage } from '@/components/artifacts-page';
import { AppConnectScreen } from '@/features/apps/app-connect-screen';
import {
  type ArtifactTypeEntry,
  EMPTY_WORKBENCH,
  type PluginPanel,
  type PluginWorkbench,
} from '@/features/plugins/plugin-workbench';
import { PluginWorkbenchContext } from '@/features/plugins/use-plugin-workbench';
import { LiveCallPanel } from '@/features/voice-call/live-call';
import { LiveScreen } from '@/features/voice-call/live-panel';
import { previewLiveCall } from '@/features/voice-call/live-preview-call';
import {
  createWorkspaceDirectoryStore,
  setWorkspaceDirectoryStore,
} from '@/lib/assistants/directory';
import { PREVIEW_PROJECTS, PREVIEW_WORKSPACE_DIRECTORY } from '@/lib/assistants/preview-directory';
import { ConversationOpenerContext } from '@/lib/desktop-shell/conversation-opener';
import { createDesktopShellStore } from '@/lib/desktop-shell/store';
import { DesktopShellStoreProvider } from '@/lib/desktop-shell/store-context';

const minutesAgo = (minutes: number) => new Date(Date.now() - minutes * 60_000).toISOString();

function artifact(
  id: string,
  title: string,
  plugin: string,
  type: string,
  typeTitle: string,
  path: string,
  modifiedAt: string,
  extra: Partial<ArtifactListingEntry> = {},
): ArtifactListingEntry {
  return {
    id,
    plugin,
    pluginTitle: plugin,
    type,
    typeTitle,
    space: 'workspace',
    path,
    conversation: null,
    workspace: 'preview',
    title,
    description: null,
    size: 1,
    modifiedAt,
    view: null,
    edit: false,
    publish: false,
    publishedRevision: null,
    entry: path,
    template: false,
    duplicate: null,
    ...extra,
  };
}

/** The boards' Library (D26): six artifacts, then what the apps ship (templates, design systems). */
const ARTIFACTS: ArtifactListingEntry[] = [
  artifact(
    'hero',
    'Hero · 3 variants',
    'com.example.design',
    'com.example.design/design',
    'Design',
    '.crowdex/design/hero',
    minutesAgo(0),
  ),
  artifact(
    'teaser',
    'Teaser',
    'com.example.video',
    'com.example.video/project',
    'Video',
    '.crowdex/video/teaser',
    minutesAgo(3),
  ),
  artifact(
    'ci',
    'CI duration',
    'com.example.dashboards',
    'com.example.dashboards/dashboard',
    'Dashboard',
    '.crowdex/dashboards/ci.json',
    minutesAgo(8),
  ),
  artifact(
    'mobile',
    'Mobile — propositions',
    'core',
    'core/document',
    'Document',
    'docs/mobile-propositions.md',
    minutesAgo(60),
  ),
  artifact(
    'budget',
    'Budget Q4',
    'com.example.sheets',
    'com.example.sheets/workbook',
    'Sheet',
    'budget-q4.xlsx',
    minutesAgo(26 * 60),
  ),
  artifact(
    'brief',
    'Morning brief',
    'com.example.automation',
    'com.example.automation/workflow',
    'Automation',
    '.crowdex/automations/brief.json',
    minutesAgo(3 * 24 * 60),
  ),
  artifact(
    'landing',
    'Landing template',
    'com.example.design',
    'com.example.design/design',
    'Design',
    'templates/landing',
    minutesAgo(5),
    { template: true, space: 'plugin' },
  ),
  ...['Crowdex', 'Editorial', 'Material', 'Paper'].map((name) =>
    artifact(
      `system-${name.toLowerCase()}`,
      `${name} design system`,
      'com.example.design',
      'com.example.design/system',
      'Design system',
      `design-systems/${name.toLowerCase()}`,
      minutesAgo(5),
      { space: 'plugin' },
    ),
  ),
  ...['Pitch deck', 'Pricing page'].map((name) =>
    artifact(
      `design-${name.toLowerCase().replace(' ', '-')}`,
      name,
      'com.example.design',
      'com.example.design/design',
      'Design',
      `templates/${name.toLowerCase().replace(' ', '-')}`,
      minutesAgo(5),
      { template: true, space: 'plugin' },
    ),
  ),
  ...['Product launch', 'Explainer', 'Changelog'].map((name) =>
    artifact(
      `video-${name.toLowerCase().replace(' ', '-')}`,
      name,
      'com.example.video',
      'com.example.video/project',
      'Video',
      `templates/${name.toLowerCase().replace(' ', '-')}`,
      minutesAgo(5),
      { template: true, space: 'plugin' },
    ),
  ),
  artifact(
    'dashboard-kpis',
    'KPI overview',
    'com.example.dashboards',
    'com.example.dashboards/dashboard',
    'Dashboard',
    'templates/kpis.json',
    minutesAgo(5),
    { template: true, space: 'plugin' },
  ),
  // The Automations extension's 21 workflow templates.
  ...[
    'Approval request',
    'Careful',
    'Content publishing',
    'Customer onboarding',
    'Daily digest',
    'Daily status',
    'Inbox triage',
    'Incident response',
    'Incident to postmortem',
    'Invoice processing',
    'Issue to production',
    'Issue triage',
    'Lead enrichment',
    'Meeting brief',
    'Produce and review',
    'Quick',
    'Security alert to fix',
    'Support reply',
    'Team work',
    'Weekly release',
    'Weekly report',
  ].map((name) =>
    artifact(
      `automation-${name.toLowerCase().replaceAll(' ', '-')}`,
      name,
      'com.example.automation',
      'com.example.automation/workflow',
      'Automation',
      `templates/${name.toLowerCase().replaceAll(' ', '-')}.json`,
      minutesAgo(5),
      { template: true, space: 'plugin' },
    ),
  ),
  ...['Release notes', 'Triage crow issues', 'Support follow-up'].map((name, index) =>
    artifact(
      `automation-own-${index}`,
      name,
      'com.example.automation',
      'com.example.automation/workflow',
      'Automation',
      `.crowdex/automations/${name.toLowerCase().replaceAll(' ', '-')}.json`,
      minutesAgo((index + 1) * 90),
    ),
  ),
];

function app(
  plugin: string,
  title: string,
  icon: string,
  types: ArtifactTypeEntry[],
  extra: Partial<PluginPanel> = {},
): PluginPanel {
  return {
    id: 'page',
    title,
    icon,
    plugin,
    view: `plugin:${plugin}/page`,
    version: 'preview',
    rail: {},
    appView: { plugin, path: '/' },
    artifactTypes: types,
    ...extra,
  };
}

const WORKBENCH: PluginWorkbench = {
  ...EMPTY_WORKBENCH,
  loaded: true,
  panels: [
    app(
      'com.example.design',
      'Design',
      'palette',
      [
        {
          type: 'com.example.design/design',
          title: 'Design',
          // A guided creation: "New design" runs it, the home's composer starts one.
          create: {
            skill: 'create-design',
            uri: 'skill://com.example.design/create-design/SKILL.md',
          },
        },
      ],
      {
        examples: [
          {
            title: 'A landing page',
            prompt:
              'Design a landing page for my product, with a hero, three features and pricing.',
          },
          { title: 'A pitch deck', prompt: 'Design a ten-slide pitch deck for my startup.' },
        ],
      },
    ),
    app('com.example.video', 'Video', 'video', [
      { type: 'com.example.video/project', title: 'Video' },
    ]),
    app('com.example.dashboards', 'Dashboards', 'chart-column', [
      { type: 'com.example.dashboards/dashboard', title: 'Dashboard' },
    ]),
    app('com.example.sheets', 'Sheets', 'file-text', [
      { type: 'com.example.sheets/workbook', title: 'Sheet' },
    ]),
    app(
      'com.example.automation',
      'Automations',
      'workflow',
      [{ type: 'com.example.automation/workflow', title: 'Automation' }],
      {
        // As its manifest declares it: one section, Workflows (no Runs since the editor, #142).
        rail: { sidebars: [{ id: 'automations', title: 'Automations', route: '/sidebar' }] },
        examples: [
          {
            title: 'Weekly report',
            prompt:
              "Create an automation that every Monday at 9:00 summarizes last week's merged pull requests and posts it to me.",
          },
          {
            title: 'Triage new issues',
            prompt:
              'Create an automation that labels and prioritizes each new GitHub issue of my repository.',
          },
        ],
      },
    ),
  ],
};

const APP_OF: Record<string, string> = {
  designs: 'com.example.design',
  dashboards: 'com.example.dashboards',
  videos: 'com.example.video',
  automations: 'com.example.automation',
};

/** The desktop frame of the boards: the sidebar's column (the shell's, lot 3) and the page. */
function Frame({ children }: { children: ReactNode }) {
  return (
    <div
      className="desktop-shell flex h-dvh overflow-hidden bg-background text-foreground"
      data-theme="desktop-dark"
    >
      <aside aria-hidden className="w-[300px] shrink-0 bg-desktop-sidebar max-sm:hidden" />
      <div className="relative flex min-w-0 flex-1 flex-col">{children}</div>
      <AppToaster />
    </div>
  );
}

/** A call in the boards' place: "Accélération CI" of the Infra & CI project. */
function useLiveShell() {
  const [shell] = useState(() => {
    const infra = PREVIEW_PROJECTS[2];
    const child = '7e0a1b2c-3d4e-4f50-8a61-9b7c8d9e0f12';
    setWorkspaceDirectoryStore(
      createWorkspaceDirectoryStore(undefined, {
        ...PREVIEW_WORKSPACE_DIRECTORY,
        placements: infra
          ? {
              [child]: {
                workspaceId: infra.workspaceId ?? null,
                projectId: infra.id,
                parentConversationId: infra.conversationId,
                workflowRun: null,
              },
            }
          : {},
      }),
    );
    const store = createDesktopShellStore();
    store.getState().setChatId(child);
    store.getState().setSyncedThreads([
      {
        id: child,
        runtime: 'cloud',
        workspaceId: 'preview',
        title: 'Accélération CI',
        updatedAt: minutesAgo(8),
        lastMessageAt: null,
        messageRevision: 0,
      },
    ]);
    return {
      store,
      opener: { openConversation: async () => false },
    };
  });
  return shell;
}

function LivePreview({ pip }: { pip: boolean }) {
  const { store, opener } = useLiveShell();
  return (
    <DesktopShellStoreProvider store={store}>
      <ConversationOpenerContext.Provider value={opener}>
        {pip ? (
          <div className="flex size-full items-center justify-center text-muted-foreground text-sm">
            The conversation, with the call in picture in picture.
            <LiveCallPanel call={previewLiveCall()} />
          </div>
        ) : (
          <LiveScreen call={previewLiveCall()} />
        )}
      </ConversationOpenerContext.Provider>
    </DesktopShellStoreProvider>
  );
}

/** The Pages lot's screens with the boards' data, without sign-in (`/desktop-preview/pages/…`). */
export function PagesPreview({ screen }: { screen: string }) {
  return (
    <PluginWorkbenchContext.Provider value={WORKBENCH}>
      <Frame>
        {screen === 'app-setup' ? (
          <AppConnectScreen
            description="Connect Crowdex to a Crowbox instance: its mailboxes for the agents, through its MCP server, and its mail client."
            plugin="com.example.crowbox"
            title="Crowbox"
          />
        ) : screen === 'live' || screen === 'pip' ? (
          <LivePreview pip={screen === 'pip'} />
        ) : screen === 'library-error' ? (
          // Reads the real index, which a preview without sign-in is refused.
          <ArtifactsPage workbench={WORKBENCH} />
        ) : APP_OF[screen] !== undefined ? (
          <AppHomePage artifacts={ARTIFACTS} plugin={APP_OF[screen]} workbench={WORKBENCH} />
        ) : (
          <ArtifactsPage artifacts={ARTIFACTS} workbench={WORKBENCH} />
        )}
      </Frame>
    </PluginWorkbenchContext.Provider>
  );
}
